import { useCallback, useEffect, useMemo, useState } from 'react'

import { useUnit } from 'effector-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Error, useErrorReset } from '@/components/ui/Error'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { Separator } from '@/components/ui/separator'
import { TextField } from '@/components/ui/text-field.tsx'
import { $account } from '@/models/account.ts'
import { CreateListDialog } from '@/modules/tasks/components/create-list-dialog'
import { AssigneeSelector } from '@/modules/tasks/components/create-task-dialog/assignee-selector'
import { DueDateSelector } from '@/modules/tasks/components/create-task-dialog/due-date-selector'
import { ListSelector } from '@/modules/tasks/components/create-task-dialog/list-selector'
import type { TaskEditorValues } from '@/modules/tasks/components/create-task-dialog/schema'
import {
  defaultValues,
  resolver,
} from '@/modules/tasks/components/create-task-dialog/schema'
import type { NavigationEntry } from '@/modules/tasks/components/task-navigation'
import { useSaveRegularTask } from '@/modules/tasks/query/use-save-task.ts'
import type { List } from '@/modules/tasks/service/task-list-service'

export interface TaskEditorDialogProps {
  task?: NavigationEntry
  isOpen: boolean
  onVisibilityChange: (state: boolean) => void
  taskList?: List
}

export const TaskEditorDialog = (props: TaskEditorDialogProps) => {
  const { isOpen, onVisibilityChange, task, taskList } = props
  const account = useUnit($account)

  const handleClose = useCallback(
    () => onVisibilityChange(false),
    [onVisibilityChange],
  )

  const form = useForm<TaskEditorValues>({
    defaultValues: {
      ...defaultValues,
      ...task,
      assignees: task?.assignees?.map((it) => it.id) ?? [],
      taskList,
    },
    resolver,
  })

  useEffect(() => {
    form.reset({
      ...defaultValues,
      ...task,
      assignees: task?.assignees?.map((it) => it.id) ?? [],
      taskList: taskList,
    })
  }, [task, taskList, form, isOpen, account])

  const handleSaved = useCallback(() => {
    form.reset()
    handleClose()
  }, [form, handleClose])

  const save = useSaveRegularTask()

  const handleSave = useCallback(
    (values: TaskEditorValues) => {
      save.mutate(values).then(([status]) => {
        if (status === 'Ok') {
          handleSaved()
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const [error, resetError] = useErrorReset(save.error)

  useEffect(() => resetError(), [isOpen, resetError])

  useEffect(() => {
    if (taskList) {
      form.setValue('taskList', taskList)
    }
  }, [form, taskList])

  const [showCreateList, setShowCreateList] = useState(false)

  const handleCloseCreateListDialog = useCallback(() => {
    setShowCreateList(false)
    onVisibilityChange(true)
  }, [onVisibilityChange])

  const handleShowCreateListDialog = useCallback(() => {
    onVisibilityChange(false)
    setShowCreateList(true)
  }, [onVisibilityChange])

  const mode = useMemo<'create' | 'edit'>(
    () => (task ? 'edit' : 'create'),
    [task],
  )

  return (
    <>
      <Dialog
        panelClassName="sm:w-full sm:mx-2 sm:max-w-[515px]"
        show={isOpen}
        onClose={handleClose}
      >
        <FormProvider {...form}>
          <Dialog.Header>
            <Dialog.Title className="text-center">
              {mode === 'edit' ? 'Edit Task' : 'Create new Task'}
            </Dialog.Title>
          </Dialog.Header>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <div className="mt-[24px] flex flex-col gap-[24px]">
              <Controller
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="name-field"
                      className="text-sm font-medium text-title"
                    >
                      Title
                    </label>
                    <TextField
                      id="name-field"
                      placeholder="Enter Title"
                      {...field}
                    />
                    {error && (
                      <p className="text-xs text-danger-default">
                        {error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                name="detail"
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="detail-field"
                      className="text-sm font-medium text-title"
                    >
                      Note
                    </label>
                    <textarea
                      id="detail-field"
                      rows={5}
                      className="w-full rounded-md border bg-card px-3 py-2 text-title focus:border-primary-500 focus:outline-0 focus:outline-primary-500"
                      placeholder="Enter Note"
                      {...field}
                    />
                    {error && (
                      <p className="text-xs text-danger-default">
                        {error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <AssigneeSelector />
              <Separator />
              <DueDateSelector />
              {!taskList && (
                <>
                  <Separator />
                  <ListSelector onOpenCreateLink={handleShowCreateListDialog} />
                </>
              )}
            </div>
            <Error
              error={error}
              closable
              className="mt-4"
            />
            <Dialog.Footer>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="w-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex w-full items-center gap-2"
                isLoading={save.isLoading}
              >
                {mode === 'edit' ? 'Save' : 'Create Task'}
              </Button>
            </Dialog.Footer>
          </form>
        </FormProvider>
      </Dialog>
      <CreateListDialog
        isOpen={showCreateList}
        onClose={handleCloseCreateListDialog}
      />
    </>
  )
}
